// src/pages/AICompliance.tsx — AI Compliance Module
// Six panels: Standards Library · BOM Compliance · Cable Classifier
//             Drawing BOM Extractor · WI Compliance Checker · WI Estimating Engine
// Powered by: Anthropic Claude API (simulated in prototype)
import { useState, useMemo } from 'react'
import { useAuthStore } from '../stores/auth.store'
import {
  MOCK_CLIENT_SPECS, SOURCE_META, SPEC_STATUS_META, TRADE_RATES,
  TRADE_RATE_MAP, calcStepCost, ClientSpec,
} from '../utils/mockDataClientSpecs'
import {
  ALL_WORK_INSTRUCTIONS, DISCIPLINE_META, WorkInstruction, getHoldPoints,
  getStepCompletionCount,
} from '../utils/mockDataWorkInstructions'
import { MOCK_JOBS } from '../utils/mockData'
import { formatCurrency, formatDate } from '../utils/formatters'
import {
  Shield, Search, Plus, X, CheckCircle, AlertTriangle, Clock,
  RefreshCw, Upload, ChevronRight, ChevronDown, ChevronUp,
  Zap, FileText, BarChart2, BookOpen, Wrench, Download,
  AlertCircle, Info, Cpu, DollarSign, Users, Calendar,
} from 'lucide-react'

// ─────────────────────────────────────────────────────────────────
// TYPES & CONSTANTS
// ─────────────────────────────────────────────────────────────────

type ComplianceResult  = 'PASS' | 'FAIL' | 'REVIEW' | 'PENDING'
type WIComplianceResult = 'COMPLIANT' | 'NON_COMPLIANT' | 'REVIEW' | 'NOT_CHECKED'
type CableClass        = 'POWER' | 'COMMS'

const RESULT_META: Record<ComplianceResult, { label: string; bg: string; text: string; dot: string }> = {
  PASS:    { label: 'Pass',        bg: 'bg-green-100',  text: 'text-green-700',  dot: 'bg-green-500'  },
  FAIL:    { label: 'Fail',        bg: 'bg-red-100',    text: 'text-red-700',    dot: 'bg-red-500'    },
  REVIEW:  { label: 'Review',      bg: 'bg-amber-100',  text: 'text-amber-700',  dot: 'bg-amber-400'  },
  PENDING: { label: 'Pending',     bg: 'bg-slate-100',  text: 'text-slate-500',  dot: 'bg-slate-300'  },
}

const WI_RESULT_META: Record<WIComplianceResult, { label: string; bg: string; text: string; icon: any }> = {
  COMPLIANT:     { label: 'Compliant',       bg: 'bg-green-100',  text: 'text-green-700',  icon: CheckCircle   },
  NON_COMPLIANT: { label: 'Non-compliant',   bg: 'bg-red-100',    text: 'text-red-700',    icon: AlertCircle   },
  REVIEW:        { label: 'Review required', bg: 'bg-amber-100',  text: 'text-amber-700',  icon: AlertTriangle },
  NOT_CHECKED:   { label: 'Not checked',     bg: 'bg-slate-100',  text: 'text-slate-500',  icon: Clock         },
}

// ─────────────────────────────────────────────────────────────────
// INLINE MOCK COMPLIANCE DATA
// ─────────────────────────────────────────────────────────────────

interface BOMComplianceItem {
  id: string; jobId: string; jobNumber: string
  description: string; manufacturer: string; partNumber: string
  category: string; standardRef: string; result: ComplianceResult
  confidence: 'HIGH' | 'MEDIUM' | 'LOW'; reason: string; checkedAt: string
}

const MOCK_BOM_RESULTS: BOMComplianceItem[] = [
  { id: 'bc1', jobId: 'j1', jobNumber: 'TP-2026-001', description: 'NGSC Controller — Aldridge Q-Tran IQ-500', manufacturer: 'Aldridge Traffic Systems', partNumber: 'IQ500-V3', category: 'Traffic Signal Equipment', standardRef: 'MRTS252', result: 'PASS', confidence: 'HIGH', reason: 'Aldridge IQ-500 is on the TMR pre-qualified products list for MRTS252 NGSC controllers. Version 3 firmware meets all specification requirements.', checkedAt: '2026-06-20T08:00:00Z' },
  { id: 'bc2', jobId: 'j1', jobNumber: 'TP-2026-001', description: '90mm UPVC conduit — orange, medium duty', manufacturer: 'Iplex Pipelines', partNumber: 'UPVC90-MD', category: 'Conduit', standardRef: 'MRTS91', result: 'PASS', confidence: 'HIGH', reason: 'Iplex 90mm medium duty UPVC meets MRTS91 cl. 4.2.1 conduit specification. Orange colour compliant for underground electrical.', checkedAt: '2026-06-20T08:01:00Z' },
  { id: 'bc3', jobId: 'j1', jobNumber: 'TP-2026-001', description: 'LED signal lantern — green, 300mm circular', manufacturer: 'Swarco Australia', partNumber: 'LED300-G-V2', category: 'Traffic Signal Lantern', standardRef: 'MRTS253', result: 'PASS', confidence: 'HIGH', reason: 'Swarco LED300 series is pre-qualified under MRTS253. Green 300mm circular variant meets intensity, chromacity, and uniformity requirements.', checkedAt: '2026-06-20T08:02:00Z' },
  { id: 'bc4', jobId: 'j1', jobNumber: 'TP-2026-001', description: '25mm2 XLPE power cable — 0.6/1kV', manufacturer: 'Prysmian Group', partNumber: 'XLPE25-0.6/1kV', category: 'Power Cable', standardRef: 'MRTS256', result: 'REVIEW', confidence: 'MEDIUM', reason: 'XLPE specification matches MRTS256. However conduit fill calculation needs verification — confirm that 25mm² cables in 90mm conduit meets the 40% maximum CSA fill requirement before proceeding.', checkedAt: '2026-06-20T08:03:00Z' },
  { id: 'bc5', jobId: 'j1', jobNumber: 'TP-2026-001', description: 'Cat6A shielded cable — SCATS loop detector', manufacturer: 'CommScope', partNumber: 'CS-CAT6A-STP', category: 'Communications Cable', standardRef: 'MRTS234', result: 'FAIL', confidence: 'HIGH', reason: 'FAIL: MRTS234 cl. 4.1 requires OS2 single-mode fibre optic cable for SCATS backbone connections. Cat6A copper cable does not comply. Substitute with OS2 single-mode fibre — e.g. Prysmian OS2-4F-LSZH.', checkedAt: '2026-06-20T08:04:00Z' },
  { id: 'bc6', jobId: 'j4', jobNumber: 'TP-2026-004', description: '33kV XLPE cable — 185mm² single core', manufacturer: 'Olex Australia', partNumber: 'HV33-185-XLPE', category: 'HV Power Cable', standardRef: 'ENX-TEC-2241', result: 'PASS', confidence: 'HIGH', reason: 'Olex HV33 185mm² XLPE is an approved product under Energex TEC-2241. XLPE insulation rated to 33kV meets all specification requirements.', checkedAt: '2026-06-21T08:00:00Z' },
  { id: 'bc7', jobId: 'j4', jobNumber: 'TP-2026-004', description: 'Cable pit — B class 900×600 polymer lid', manufacturer: 'Hanson Construction', partNumber: 'PIT-B-900x600', category: 'Pit and Lid', standardRef: 'MRTS91', result: 'REVIEW', confidence: 'MEDIUM', reason: 'MRTS91 requires AASHTO H20 load rating for pits in trafficked areas. Confirm that the Hanson B class polymer lid carries the required 80kN wheel load rating for the pit\'s specific location (trafficked vs non-trafficked).', checkedAt: '2026-06-21T08:01:00Z' },
]

interface CableItem {
  id: string; jobId: string; jobNumber: string
  description: string; classification: CableClass
  method: 'RULES_ENGINE' | 'CLAUDE_API'
  confidence: 'HIGH' | 'MEDIUM' | 'LOW'; linkedStandard: string
}

const MOCK_CABLE_ITEMS: CableItem[] = [
  { id: 'ca1', jobId: 'j1', jobNumber: 'TP-2026-001', description: '25mm² XLPE power cable 0.6/1kV — mains supply to controller', classification: 'POWER', method: 'RULES_ENGINE', confidence: 'HIGH', linkedStandard: 'MRTS256' },
  { id: 'ca2', jobId: 'j1', jobNumber: 'TP-2026-001', description: 'OS2 single-mode fibre 4-core LSZH — SCATS backbone', classification: 'COMMS', method: 'RULES_ENGINE', confidence: 'HIGH', linkedStandard: 'MRTS234' },
  { id: 'ca3', jobId: 'j1', jobNumber: 'TP-2026-001', description: '4mm² twin + earth control cable — pedestrian push button', classification: 'POWER', method: 'RULES_ENGINE', confidence: 'HIGH', linkedStandard: 'MRTS256' },
  { id: 'ca4', jobId: 'j1', jobNumber: 'TP-2026-001', description: 'Multicore instrumentation cable 12-pair overall shielded', classification: 'COMMS', method: 'CLAUDE_API',  confidence: 'MEDIUM', linkedStandard: 'MRTS234' },
  { id: 'ca5', jobId: 'j4', jobNumber: 'TP-2026-004', description: '33kV single-core XLPE 185mm² aluminium — HV service', classification: 'POWER', method: 'RULES_ENGINE', confidence: 'HIGH', linkedStandard: 'ENX-TEC-2241' },
  { id: 'ca6', jobId: 'j4', jobNumber: 'TP-2026-004', description: 'Cat5e UTP patch cable — site comms', classification: 'COMMS', method: 'RULES_ENGINE', confidence: 'HIGH', linkedStandard: 'MRTS234' },
]

// WI Compliance: estimate vs WI requirements
interface WIGap {
  type: 'CREW' | 'TRADE' | 'PLANT' | 'TIME' | 'HOLD_POINT' | 'SEQUENCE'
  description: string; severity: 'HIGH' | 'MEDIUM' | 'LOW'
}

interface WIComplianceCheck {
  id: string; wiId: string; wiRef: string; wiTitle: string
  jobId: string; jobNumber: string; result: WIComplianceResult
  estimatedHours: number; wiRequiredHours: number
  estimatedCrew: number; wiMinCrew: number
  gaps: WIGap[]
  checkedAt: string
}

const MOCK_WI_COMPLIANCE: WIComplianceCheck[] = [
  {
    id: 'wc1', wiId: 'wi-sop-001', wiRef: 'WI-SOP-CIV-001', wiTitle: 'Signal pole foundation — install and backfill',
    jobId: 'j1', jobNumber: 'TP-2026-001', result: 'COMPLIANT',
    estimatedHours: 4.0, wiRequiredHours: 4.0, estimatedCrew: 3, wiMinCrew: 2,
    gaps: [], checkedAt: '2026-06-20T09:00:00Z',
  },
  {
    id: 'wc2', wiId: 'wi-sop-003', wiRef: 'WI-SOP-ELEC-001', wiTitle: 'Traffic signal — electrical installation',
    jobId: 'j1', jobNumber: 'TP-2026-001', result: 'REVIEW',
    estimatedHours: 6.0, wiRequiredHours: 8.0, estimatedCrew: 2, wiMinCrew: 2,
    gaps: [
      { type: 'TIME', description: 'Estimated duration (6.0 hrs) is 75% of WI allowance (8.0 hrs). Time compression below 80% threshold — confirm site conditions support accelerated completion.', severity: 'MEDIUM' },
    ],
    checkedAt: '2026-06-20T09:01:00Z',
  },
  {
    id: 'wc3', wiId: 'wi-sop-004', wiRef: 'WI-SOP-HV-001', wiTitle: 'HV cable jointing — 11kV heat shrink',
    jobId: 'j4', jobNumber: 'TP-2026-004', result: 'NON_COMPLIANT',
    estimatedHours: 6.0, wiRequiredHours: 12.0, estimatedCrew: 1, wiMinCrew: 2,
    gaps: [
      { type: 'CREW', description: 'Crew size: estimate has 1 worker — WI-SOP-HV-001 requires minimum 2 (1× HV jointer + 1× electrical fitter). Add 1× EL1 electrical fitter.', severity: 'HIGH' },
      { type: 'TRADE', description: 'Estimate has EL1 generic electrician — WI requires a qualified HV jointer (HV licence) for the jointing works. Upgrade trade allocation to HV-licensed jointer.', severity: 'HIGH' },
      { type: 'TIME', description: 'Estimated duration (6.0 hrs) is 50% of WI allowance (12.0 hrs per joint in confined pit). Below 60% threshold — commercial risk: estimate is likely unprofitable.', severity: 'HIGH' },
    ],
    checkedAt: '2026-06-20T09:02:00Z',
  },
  {
    id: 'wc4', wiId: 'wi-sop-006', wiRef: 'WI-SOP-COM-001', wiTitle: 'ITS commissioning — SCATS configuration',
    jobId: 'j1', jobNumber: 'TP-2026-001', result: 'COMPLIANT',
    estimatedHours: 16.0, wiRequiredHours: 14.0, estimatedCrew: 2, wiMinCrew: 2,
    gaps: [], checkedAt: '2026-06-20T09:03:00Z',
  },
]

// ─────────────────────────────────────────────────────────────────
// SHARED SMALL COMPONENTS
// ─────────────────────────────────────────────────────────────────

function ResultBadge({ result }: { result: ComplianceResult }) {
  const m = RESULT_META[result]
  return <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-0.5 rounded-full ${m.bg} ${m.text}`}>
    <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />{m.label}
  </span>
}

function WIResultBadge({ result }: { result: WIComplianceResult }) {
  const m = WI_RESULT_META[result]
  const Icon = m.icon
  return <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-0.5 rounded-full ${m.bg} ${m.text}`}>
    <Icon className="w-3 h-3" />{m.label}
  </span>
}

function AiTag({ method }: { method: 'RULES_ENGINE' | 'CLAUDE_API' }) {
  return method === 'CLAUDE_API'
    ? <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full flex items-center gap-1"><Cpu className="w-3 h-3" />Claude API</span>
    : <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">Rules engine</span>
}

function ConfidenceChip({ c }: { c: 'HIGH' | 'MEDIUM' | 'LOW' }) {
  const map = { HIGH: 'text-green-700 bg-green-50', MEDIUM: 'text-amber-700 bg-amber-50', LOW: 'text-red-700 bg-red-50' }
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${map[c]}`}>{c}</span>
}

function GapRow({ gap }: { gap: WIGap }) {
  const sev = { HIGH: 'text-red-600 bg-red-50 border-red-200', MEDIUM: 'text-amber-700 bg-amber-50 border-amber-200', LOW: 'text-blue-700 bg-blue-50 border-blue-200' }
  const icons = { CREW: '👷', TRADE: '🪪', PLANT: '🏗', TIME: '⏱', HOLD_POINT: '🔴', SEQUENCE: '🔢' }
  return (
    <div className={`flex items-start gap-2 text-xs rounded-lg px-3 py-2 border ${sev[gap.severity]}`}>
      <span className="flex-shrink-0">{icons[gap.type]}</span>
      <span className="leading-relaxed">{gap.description}</span>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// TAB 1: STANDARDS LIBRARY
// ─────────────────────────────────────────────────────────────────

function StandardsLibraryTab({ canManage }: { canManage: boolean }) {
  const [search, setSearch]         = useState('')
  const [sourceFilter, setSource]   = useState('ALL')
  const [expanded, setExpanded]     = useState<string | null>(null)
  const [showImport, setShowImport] = useState(false)

  const filtered = useMemo(() =>
    MOCK_CLIENT_SPECS.filter(s => {
      const q = search.toLowerCase()
      const matchQ = !q || s.reference.toLowerCase().includes(q) || s.title.toLowerCase().includes(q)
      const matchS = sourceFilter === 'ALL' || s.source === sourceFilter
      return matchQ && matchS
    }),
    [search, sourceFilter]
  )

  const updateCount = MOCK_CLIENT_SPECS.filter(s => s.updateAvailableFlag).length
  const sources = Array.from(new Set(MOCK_CLIENT_SPECS.map(s => s.source)))

  return (
    <div className="space-y-4">
      {updateCount > 0 && (
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <RefreshCw className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-800 flex-1">
            <strong>{updateCount} standard{updateCount !== 1 ? 's' : ''}</strong> have newer versions available. Review and confirm before compliance checking updates.
          </p>
          <button className="text-xs text-amber-700 underline">Review updates</button>
        </div>
      )}

      <div className="flex gap-3 flex-col sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search reference, title…" className="input pl-9 w-full text-sm" />
        </div>
        <select value={sourceFilter} onChange={e => setSource(e.target.value)} className="input text-sm">
          <option value="ALL">All sources</option>
          {sources.map(s => <option key={s} value={s}>{SOURCE_META[s]?.label ?? s}</option>)}
        </select>
        {canManage && (
          <button onClick={() => setShowImport(true)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors">
            <Plus className="w-4 h-4" /> Import standard
          </button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="divide-y divide-slate-100">
          {filtered.map(spec => {
            const sm  = SOURCE_META[spec.source]
            const stm = SPEC_STATUS_META[spec.status]
            const isExp = expanded === spec.id
            return (
              <div key={spec.id}>
                <button
                  onClick={() => setExpanded(isExp ? null : spec.id)}
                  className="w-full flex items-center gap-3 px-5 py-3.5 text-left hover:bg-slate-50 transition-colors">
                  <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${spec.updateAvailableFlag ? 'bg-amber-400 animate-pulse' : stm.bg.replace('bg-', 'bg-').includes('green') ? 'bg-green-500' : 'bg-slate-300'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="font-mono text-sm font-bold text-slate-900">{spec.reference}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sm?.bg} ${sm?.text}`}>{sm?.label}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${stm.bg} ${stm.text}`}>{stm.label}</span>
                      {spec.updateAvailableFlag && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">Update available</span>}
                      {spec.hasUploadedFile && <span className="text-xs bg-green-50 text-green-600 px-2 py-0.5 rounded-full">PDF uploaded</span>}
                    </div>
                    <p className="text-sm text-slate-600 truncate">{spec.title}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs text-slate-400">v{spec.version}</span>
                    {isExp ? <ChevronUp className="w-4 h-4 text-slate-300" /> : <ChevronDown className="w-4 h-4 text-slate-300" />}
                  </div>
                </button>
                {isExp && (
                  <div className="px-5 pb-4 pt-1 bg-slate-50 border-t border-slate-100 space-y-3">
                    {spec.description && <p className="text-sm text-slate-600">{spec.description}</p>}
                    {spec.keyRequirements?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Key requirements</p>
                        <ul className="space-y-1">
                          {spec.keyRequirements.map((r, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                              <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />{r}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {spec.uploadedFileName && (
                      <p className="text-xs text-slate-400 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" />{spec.uploadedFileName}</p>
                    )}
                    <div className="flex gap-2">
                      {spec.sourceUrl && (
                        <a href={spec.sourceUrl} target="_blank" rel="noreferrer"
                          className="text-xs text-indigo-600 hover:underline flex items-center gap-1">
                          <ChevronRight className="w-3.5 h-3.5" />View on TMR website
                        </a>
                      )}
                      {spec.updateAvailableFlag && canManage && (
                        <button className="text-xs text-amber-700 bg-amber-100 hover:bg-amber-200 px-2.5 py-1 rounded-lg transition-colors ml-auto">
                          Review &amp; confirm update
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
        <div className="px-5 py-2.5 bg-slate-50 border-t text-xs text-slate-400">
          {filtered.length} of {MOCK_CLIENT_SPECS.length} standards shown · Last nightly crawl: 27 Jun 2026 02:00 AEST
        </div>
      </div>

      {showImport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">Import standard / work instruction</h3>
              <button onClick={() => setShowImport(false)}><X className="w-4 h-4 text-slate-400" /></button>
            </div>
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-3 text-sm text-indigo-700">
              In production, this imports the PDF, runs Claude API text extraction, and creates a structured standard or WI record in the database.
            </div>
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Source URL (TMR / Energex)</label>
              <input className="input w-full text-sm" placeholder="https://www.tmr.qld.gov.au/..." /></div>
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Reference code</label>
              <input className="input w-full text-sm" placeholder="e.g. MRTS93, TSI-012" /></div>
            <div className="flex gap-2">
              <button onClick={() => setShowImport(false)} className="flex-1 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={() => setShowImport(false)} className="flex-1 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium">Import &amp; parse</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// TAB 2: BOM COMPLIANCE CHECKER
// ─────────────────────────────────────────────────────────────────


// ─────────────────────────────────────────────────────────────────
// PHASE 7 — BOM COMPLIANCE CERTIFICATE
// Formal certificate confirming bill-of-materials items have been
// checked against MRTS / Energex TEC / AS-NZS standards. Required
// as evidence for client handover, tender qualification, or audit.
// ─────────────────────────────────────────────────────────────────
function generateBOMComplianceCertificate(jobId: string, jobNumber: string, results: BOMComplianceItem[]) {
  const now = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const jobResults = results.filter(r => r.jobId === jobId)
  const passCount = jobResults.filter(r => r.result === 'PASS').length
  const failCount = jobResults.filter(r => r.result === 'FAIL').length
  const reviewCount = jobResults.filter(r => r.result === 'REVIEW').length
  const allClear = failCount === 0 && reviewCount === 0

  const rows = jobResults.map((r, i) => {
    const rc = r.result === 'PASS' ? '#166534' : r.result === 'FAIL' ? '#b91c1c' : '#d97706'
    return `<tr style="background:${i%2===0?'#fff':'#f9fafb'}">
      <td style="padding:6px 10px;font-size:9.5pt">${r.description}</td>
      <td style="padding:6px 8px;font-size:9pt">${r.manufacturer}</td>
      <td style="padding:6px 8px;font-family:monospace;font-size:8.5pt">${r.partNumber}</td>
      <td style="padding:6px 8px;font-size:9pt;text-align:center">${r.standardRef}</td>
      <td style="padding:6px 8px;text-align:center;font-weight:bold;color:${rc}">${r.result}</td>
    </tr>`
  }).join('')

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>BOM Compliance Certificate — ${jobNumber}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10.5pt;color:#111;margin:20mm}
h1{font-size:20pt;font-weight:900;color:#1B3A6B;margin:0}
.hdr{border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:18px;display:flex;justify-content:space-between;align-items:flex-end}
.cert-banner{border-radius:6px;padding:14px 20px;margin:14px 0;text-align:center;font-size:13pt;font-weight:900}
.cert-pass{background:#dcfce7;border:2px solid #86efac;color:#166534}
.cert-fail{background:#fee2e2;border:2px solid #fca5a5;color:#b91c1c}
.kpi{display:flex;gap:10px;margin-bottom:14px}
.kpi-box{flex:1;border:1px solid #e5e7eb;border-radius:5px;padding:8px;text-align:center}
.kpi-v{font-size:18pt;font-weight:900}
table{width:100%;border-collapse:collapse;font-size:9.5pt;margin-bottom:14px}
th{background:#1B3A6B;color:#fff;padding:6px 10px;text-align:left}
.statement{border:1px solid #ccc;border-radius:4px;padding:12px 16px;background:#fafafa;margin:14px 0;font-size:10pt;line-height:1.7}
.sig-block{margin-top:22px}
.sig-line{border-bottom:1.5px solid #333;height:32px;margin-bottom:4px}
.sig-lbl{font-size:8pt;color:#888}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:18px;border-top:1px solid #eee;padding-top:7px}
@media print{body{margin:15mm}}
</style></head><body>
<div class="hdr">
  <div><h1>MCB Civil Electrical</h1><div style="font-size:9pt;color:#888">ABN 44 128 993 087 · 14 Commerce Dr, Salisbury QLD 4107</div></div>
  <div style="text-align:right"><div style="font-size:9pt;color:#888">Date: ${now}</div></div>
</div>

<div style="font-size:16pt;font-weight:900;color:#1B3A6B;text-align:center;margin-bottom:4px">BILL OF MATERIALS COMPLIANCE CERTIFICATE</div>
<div style="text-align:center;color:#666;font-size:10pt;margin-bottom:14px">Project: ${jobNumber}</div>

<div class="cert-banner ${allClear ? 'cert-pass' : 'cert-fail'}">
  ${allClear ? '✓ ALL MATERIALS COMPLIANT' : `⚠ ${failCount + reviewCount} ITEM(S) REQUIRE ATTENTION BEFORE CERTIFICATION`}
</div>

<div class="kpi">
  <div class="kpi-box"><div class="kpi-v" style="color:#166534">${passCount}</div><div style="font-size:9pt;color:#6b7280">Pass</div></div>
  <div class="kpi-box"><div class="kpi-v" style="color:#d97706">${reviewCount}</div><div style="font-size:9pt;color:#6b7280">Review</div></div>
  <div class="kpi-box"><div class="kpi-v" style="color:#b91c1c">${failCount}</div><div style="font-size:9pt;color:#6b7280">Fail</div></div>
</div>

<table>
  <thead><tr><th>Description</th><th>Manufacturer</th><th>Part number</th><th style="text-align:center">Standard</th><th style="text-align:center">Result</th></tr></thead>
  <tbody>${rows || '<tr><td colspan="5" style="padding:12px;text-align:center;color:#9ca3af">No BOM compliance checks recorded for this job</td></tr>'}</tbody>
</table>

<div class="statement">
  ${allClear
    ? `This certificate confirms that all bill-of-materials items for project ${jobNumber} have been checked against applicable specifications (MRTS, Energex TEC, AS/NZS standards as relevant) using MCB Civil Electrical's AI-assisted compliance checking system, and all items have been assessed as compliant. This certificate may be relied upon for client handover, tender qualification submissions, and quality audit purposes.`
    : `This certificate identifies ${failCount + reviewCount} bill-of-materials item(s) for project ${jobNumber} that did not pass initial compliance review against applicable specifications. These items must be resolved (substituted, re-assessed, or formally accepted with engineering sign-off) before this certificate can be issued as a clear pass.`}
</div>

<div class="sig-block">
  <div class="sig-line"></div>
  <div class="sig-lbl">Quality Manager / PM — certified compliant · name &amp; date</div>
</div>

<div class="footer">MCB Civil Electrical Pty Ltd · BOM Compliance Certificate · ${jobNumber} · Generated ${now}<br>Compliance assessed using rules engine and Claude API standards cross-reference. Retain for project quality records.</div>
</body></html>`
  const w = window.open('', '_blank', 'width=960,height=820')
  if (!w) { alert('Pop-up blocked'); return }
  w.document.write(html); w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}

// ─────────────────────────────────────────────────────────────────
// PHASE 7 — WI COMPLIANCE CERTIFICATE
// Formal certificate confirming estimate labour/crew/time allocations
// have been checked against published Work Instructions.
// ─────────────────────────────────────────────────────────────────
function generateWIComplianceCertificate(jobId: string, jobNumber: string, checks: WIComplianceCheck[]) {
  const now = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const jobChecks = checks.filter(c => c.jobId === jobId)
  const compliantCount = jobChecks.filter(c => c.result === 'COMPLIANT').length
  const reviewCount = jobChecks.filter(c => c.result === 'REVIEW').length
  const ncCount = jobChecks.filter(c => c.result === 'NON_COMPLIANT').length
  const allClear = ncCount === 0

  const rows = jobChecks.map((c, i) => {
    const rc = c.result === 'COMPLIANT' ? '#166534' : c.result === 'NON_COMPLIANT' ? '#b91c1c' : '#d97706'
    return `<tr style="background:${i%2===0?'#fff':'#f9fafb'}">
      <td style="padding:6px 10px;font-family:monospace;font-size:9pt">${c.wiRef}</td>
      <td style="padding:6px 10px;font-size:9.5pt">${c.wiTitle}</td>
      <td style="padding:6px 8px;text-align:center;font-size:9pt">${c.estimatedHours}h / ${c.wiRequiredHours}h</td>
      <td style="padding:6px 8px;text-align:center;font-size:9pt">${c.estimatedCrew} / ${c.wiMinCrew}</td>
      <td style="padding:6px 8px;text-align:center;font-size:9pt">${c.gaps.length}</td>
      <td style="padding:6px 8px;text-align:center;font-weight:bold;color:${rc}">${c.result.replace(/_/g,' ')}</td>
    </tr>`
  }).join('')

  const allGaps = jobChecks.flatMap(c => c.gaps.map(g => ({ ...g, wiRef: c.wiRef })))
  const gapRows = allGaps.map((g, i) => `
    <tr style="background:${i%2===0?'#fff':'#f9fafb'}">
      <td style="padding:5px 10px;font-family:monospace;font-size:9pt">${g.wiRef}</td>
      <td style="padding:5px 8px;font-size:9pt;font-weight:bold;color:${g.severity==='HIGH'?'#b91c1c':g.severity==='MEDIUM'?'#d97706':'#1d4ed8'}">${g.severity}</td>
      <td style="padding:5px 8px;font-size:9pt">${g.type}</td>
      <td style="padding:5px 10px;font-size:9pt">${g.description}</td>
    </tr>`).join('')

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>WI Compliance Certificate — ${jobNumber}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10.5pt;color:#111;margin:20mm}
h1{font-size:20pt;font-weight:900;color:#1B3A6B;margin:0}
h2{font-size:11pt;color:#1B3A6B;border-bottom:1.5px solid #1B3A6B;padding-bottom:3px;margin:16px 0 8px}
.hdr{border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:18px;display:flex;justify-content:space-between;align-items:flex-end}
.cert-banner{border-radius:6px;padding:14px 20px;margin:14px 0;text-align:center;font-size:13pt;font-weight:900}
.cert-pass{background:#dcfce7;border:2px solid #86efac;color:#166534}
.cert-fail{background:#fee2e2;border:2px solid #fca5a5;color:#b91c1c}
.kpi{display:flex;gap:10px;margin-bottom:14px}
.kpi-box{flex:1;border:1px solid #e5e7eb;border-radius:5px;padding:8px;text-align:center}
.kpi-v{font-size:18pt;font-weight:900}
table{width:100%;border-collapse:collapse;font-size:9.5pt;margin-bottom:14px}
th{background:#1B3A6B;color:#fff;padding:6px 10px;text-align:left}
.statement{border:1px solid #ccc;border-radius:4px;padding:12px 16px;background:#fafafa;margin:14px 0;font-size:10pt;line-height:1.7}
.sig-block{margin-top:22px}
.sig-line{border-bottom:1.5px solid #333;height:32px;margin-bottom:4px}
.sig-lbl{font-size:8pt;color:#888}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:18px;border-top:1px solid #eee;padding-top:7px}
@media print{body{margin:15mm}}
</style></head><body>
<div class="hdr">
  <div><h1>MCB Civil Electrical</h1><div style="font-size:9pt;color:#888">ABN 44 128 993 087 · 14 Commerce Dr, Salisbury QLD 4107</div></div>
  <div style="text-align:right"><div style="font-size:9pt;color:#888">Date: ${now}</div></div>
</div>

<div style="font-size:16pt;font-weight:900;color:#1B3A6B;text-align:center;margin-bottom:4px">WORK INSTRUCTION COMPLIANCE CERTIFICATE</div>
<div style="text-align:center;color:#666;font-size:10pt;margin-bottom:14px">Project: ${jobNumber}</div>

<div class="cert-banner ${allClear ? 'cert-pass' : 'cert-fail'}">
  ${allClear ? '✓ ALL WORK INSTRUCTIONS COMPLIANT' : `⚠ ${ncCount} WORK INSTRUCTION(S) NON-COMPLIANT — ESTIMATE CANNOT BE APPROVED`}
</div>

<div class="kpi">
  <div class="kpi-box"><div class="kpi-v" style="color:#166534">${compliantCount}</div><div style="font-size:9pt;color:#6b7280">Compliant</div></div>
  <div class="kpi-box"><div class="kpi-v" style="color:#d97706">${reviewCount}</div><div style="font-size:9pt;color:#6b7280">Review</div></div>
  <div class="kpi-box"><div class="kpi-v" style="color:#b91c1c">${ncCount}</div><div style="font-size:9pt;color:#6b7280">Non-compliant</div></div>
</div>

<h2>Work instruction compliance checks</h2>
<table>
  <thead><tr><th>WI ref</th><th>Title</th><th style="text-align:center">Hours (est/req)</th><th style="text-align:center">Crew (est/min)</th><th style="text-align:center">Gaps</th><th style="text-align:center">Result</th></tr></thead>
  <tbody>${rows || '<tr><td colspan="6" style="padding:12px;text-align:center;color:#9ca3af">No WI compliance checks recorded for this job</td></tr>'}</tbody>
</table>

${allGaps.length > 0 ? `<h2>Identified gaps requiring resolution</h2>
<table><thead><tr><th>WI ref</th><th style="text-align:center">Severity</th><th>Type</th><th>Description</th></tr></thead>
<tbody>${gapRows}</tbody></table>` : ''}

<div class="statement">
  ${allClear
    ? `This certificate confirms that the labour, crew, and time allocations in the estimate for project ${jobNumber} have been checked against the applicable published Work Instructions (TMR TSI, Energex EWI, or internal SOPs) using MCB Civil Electrical's AI-assisted compliance checking system, and all checks have returned a compliant result. This estimate may proceed to tender pricing or contract execution.`
    : `This certificate identifies ${ncCount} work instruction(s) for project ${jobNumber} where the estimate does not meet minimum WI requirements (crew size, trade qualification, or time allowance). Per MCB Civil Electrical's estimating governance policy, this estimate CANNOT be approved for tender submission or contract execution until all non-compliant gaps listed above are resolved.`}
</div>

<div class="sig-block">
  <div class="sig-line"></div>
  <div class="sig-lbl">Estimating Manager / PM — certified compliant · name &amp; date</div>
</div>

<div class="footer">MCB Civil Electrical Pty Ltd · WI Compliance Certificate · ${jobNumber} · Generated ${now}<br>Compliance assessed using Claude API cross-reference against published Work Instructions. Retain for project quality records.</div>
</body></html>`
  const w = window.open('', '_blank', 'width=1020,height=820')
  if (!w) { alert('Pop-up blocked'); return }
  w.document.write(html); w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}

function BOMComplianceTab() {
  const [jobFilter, setJobFilter]       = useState('ALL')
  const [resultFilter, setResultFilter] = useState('ALL')
  const [running, setRunning]           = useState(false)
  const [expanded, setExpanded]         = useState<string | null>(null)

  const results = useMemo(() =>
    MOCK_BOM_RESULTS.filter(r =>
      (jobFilter === 'ALL' || r.jobId === jobFilter) &&
      (resultFilter === 'ALL' || r.result === resultFilter)
    ),
    [jobFilter, resultFilter]
  )

  const stats = {
    pass:    MOCK_BOM_RESULTS.filter(r => r.result === 'PASS').length,
    fail:    MOCK_BOM_RESULTS.filter(r => r.result === 'FAIL').length,
    review:  MOCK_BOM_RESULTS.filter(r => r.result === 'REVIEW').length,
  }

  const handleRun = () => {
    setRunning(true)
    setTimeout(() => setRunning(false), 1800)
  }

  return (
    <div className="space-y-4">
      {/* Stats + run button */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex gap-3 flex-wrap flex-1">
          {[
            { label: 'Pass', value: stats.pass, bg: 'bg-green-50', text: 'text-green-700' },
            { label: 'Review', value: stats.review, bg: 'bg-amber-50', text: 'text-amber-700' },
            { label: 'Fail', value: stats.fail, bg: 'bg-red-50', text: 'text-red-700' },
          ].map(s => (
            <div key={s.label} className={`${s.bg} rounded-xl px-5 py-3 text-center min-w-[80px]`}>
              <p className={`text-2xl font-bold ${s.text}`}>{s.value}</p>
              <p className="text-xs text-slate-400">{s.label}</p>
            </div>
          ))}
        </div>
        <button onClick={handleRun} disabled={running}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg disabled:opacity-60 transition-colors">
          {running ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Cpu className="w-4 h-4" />}
          {running ? 'Checking…' : 'Run compliance check'}
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-col sm:flex-row">
        <select value={jobFilter} onChange={e => setJobFilter(e.target.value)} className="input text-sm">
          <option value="ALL">All projects</option>
          {MOCK_JOBS.map(j => <option key={j.id} value={j.id}>{j.jobNumber}</option>)}
        </select>
        <select value={resultFilter} onChange={e => setResultFilter(e.target.value)} className="input text-sm">
          <option value="ALL">All results</option>
          <option value="PASS">Pass</option>
          <option value="REVIEW">Review</option>
          <option value="FAIL">Fail</option>
        </select>
        {jobFilter !== 'ALL' && (
          <button onClick={() => generateBOMComplianceCertificate(jobFilter, MOCK_JOBS.find(j => j.id === jobFilter)?.jobNumber ?? jobFilter, MOCK_BOM_RESULTS)}
            className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">
            📋 BOM compliance certificate
          </button>
        )}
      </div>

      {running && (
        <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3">
          <Cpu className="w-4 h-4 text-indigo-600 animate-pulse" />
          <p className="text-sm text-indigo-700">Claude API checking BOM items against MRTS, Energex TEC, and AS/NZS standards…</p>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="divide-y divide-slate-100">
          {results.map(item => {
            const isExp = expanded === item.id
            return (
              <div key={item.id}>
                <button onClick={() => setExpanded(isExp ? null : item.id)}
                  className="w-full flex items-center gap-3 px-5 py-3.5 text-left hover:bg-slate-50 transition-colors">
                  <ResultBadge result={item.result} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{item.description}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{item.manufacturer} · {item.partNumber} · {item.standardRef} · {item.jobNumber}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <ConfidenceChip c={item.confidence} />
                    {isExp ? <ChevronUp className="w-4 h-4 text-slate-300" /> : <ChevronDown className="w-4 h-4 text-slate-300" />}
                  </div>
                </button>
                {isExp && (
                  <div className={`px-5 pb-4 pt-2 border-t ${item.result === 'FAIL' ? 'bg-red-50/40' : item.result === 'REVIEW' ? 'bg-amber-50/30' : 'bg-green-50/20'}`}>
                    <p className="text-sm text-slate-700 leading-relaxed">{item.reason}</p>
                    <p className="text-xs text-slate-400 mt-2 flex items-center gap-1.5">
                      <Cpu className="w-3 h-3" /> Checked {formatDate(item.checkedAt)} · Standard: {item.standardRef}
                    </p>
                    {(item.result === 'FAIL' || item.result === 'REVIEW') && (
                      <div className="flex gap-2 mt-3">
                        <button className="text-xs px-3 py-1.5 bg-indigo-600 text-white rounded-lg">Substitute item</button>
                        {item.result === 'REVIEW' && <button className="text-xs px-3 py-1.5 border border-slate-200 rounded-lg text-slate-600 hover:bg-white">Confirm / Override</button>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
        <div className="px-5 py-2.5 bg-slate-50 border-t text-xs text-slate-400">
          {results.length} items · Results cached 4 hrs · Cache key: standardId + itemDescription hash
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// TAB 3: CABLE CLASSIFIER
// ─────────────────────────────────────────────────────────────────

function CableClassifierTab() {
  const [jobFilter, setJobFilter] = useState('ALL')
  const items = MOCK_CABLE_ITEMS.filter(c => jobFilter === 'ALL' || c.jobId === jobFilter)
  const powerCount = items.filter(c => c.classification === 'POWER').length
  const commsCount = items.filter(c => c.classification === 'COMMS').length
  const claudeCount = items.filter(c => c.method === 'CLAUDE_API').length

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-yellow-50 rounded-xl p-4"><p className="text-2xl font-bold text-yellow-700">{powerCount}</p><p className="text-xs text-slate-400 mt-0.5">POWER cables</p><p className="text-xs text-slate-400">→ MRTS256</p></div>
        <div className="bg-blue-50 rounded-xl p-4"><p className="text-2xl font-bold text-blue-700">{commsCount}</p><p className="text-xs text-slate-400 mt-0.5">COMMS cables</p><p className="text-xs text-slate-400">→ MRTS234</p></div>
        <div className="bg-purple-50 rounded-xl p-4"><p className="text-2xl font-bold text-purple-700">{claudeCount}</p><p className="text-xs text-slate-400 mt-0.5">Claude API calls</p><p className="text-xs text-slate-400">{items.length - claudeCount} rules engine</p></div>
      </div>

      <div className="flex gap-3">
        <select value={jobFilter} onChange={e => setJobFilter(e.target.value)} className="input text-sm">
          <option value="ALL">All projects</option>
          {MOCK_JOBS.map(j => <option key={j.id} value={j.id}>{j.jobNumber}</option>)}
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b border-slate-200">
            <th className="text-left px-5 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Description</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Class</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Standard</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Method</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Confidence</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {items.map(item => (
              <tr key={item.id} className="hover:bg-slate-50">
                <td className="px-5 py-3">
                  <p className="font-medium text-slate-900 text-xs leading-snug">{item.description}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{item.jobNumber}</p>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full ${item.classification === 'POWER' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}`}>
                    {item.classification === 'POWER' ? '⚡' : '📶'} {item.classification}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-indigo-700">{item.linkedStandard}</td>
                <td className="px-4 py-3"><AiTag method={item.method} /></td>
                <td className="px-4 py-3"><ConfidenceChip c={item.confidence} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-5 py-2.5 bg-slate-50 border-t text-xs text-slate-400">
          Rules engine handles ~80% of classifications at zero API cost · Claude API used for ambiguous descriptions only
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// TAB 4: DRAWING BOM EXTRACTOR
// ─────────────────────────────────────────────────────────────────

function DrawingExtractorTab() {
  const [step, setStep]       = useState<'upload' | 'review' | 'done'>('upload')
  const [file, setFile]       = useState<File | null>(null)
  const [jobId, setJobId]     = useState(MOCK_JOBS[0]?.id ?? '')
  const [processing, setProc] = useState(false)

  const MOCK_EXTRACTED = [
    { id: 'ex1', description: '90mm UPVC conduit orange medium duty', quantity: 240, unit: 'm', confidence: 'HIGH' as const, match: 'EXACT' },
    { id: 'ex2', description: 'Signal pole — 7m Class 2 steel galvanised', quantity: 4, unit: 'EA', confidence: 'HIGH' as const, match: 'EXACT' },
    { id: 'ex3', description: 'Traffic controller cabinet — NGSC Q-Tran IQ500', quantity: 1, unit: 'EA', confidence: 'HIGH' as const, match: 'EXACT' },
    { id: 'ex4', description: 'LED signal head circular 300mm', quantity: 12, unit: 'EA', confidence: 'MEDIUM' as const, match: 'FUZZY' },
    { id: 'ex5', description: 'Pit lid type 2 heavy duty concrete', quantity: 6, unit: 'EA', confidence: 'MEDIUM' as const, match: 'FUZZY' },
    { id: 'ex6', description: 'Cable trunking 100x50 PVC white', quantity: 18, unit: 'm', confidence: 'LOW' as const, match: 'NONE' },
  ]

  const [accepted, setAccepted] = useState<Set<string>>(new Set())

  const handleUpload = () => {
    setProc(true)
    setTimeout(() => { setProc(false); setStep('review') }, 2000)
  }

  return (
    <div className="space-y-4">
      {step === 'upload' && (
        <div className="space-y-4">
          <div className="border-2 border-dashed border-slate-300 rounded-xl p-10 text-center hover:border-indigo-400 transition-colors">
            <Upload className="w-8 h-8 text-slate-300 mx-auto mb-3" />
            <p className="font-medium text-slate-600">Upload engineering drawing</p>
            <p className="text-sm text-slate-400 mt-1">PDF, IFC, PNG, or JPEG · Text-based or scanned</p>
            <input type="file" accept=".pdf,.ifc,.png,.jpg,.jpeg"
              onChange={e => setFile(e.target.files?.[0] ?? null)}
              className="mt-4 block mx-auto text-sm text-slate-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Target job</label>
            <select value={jobId} onChange={e => setJobId(e.target.value)} className="input w-full text-sm">
              {MOCK_JOBS.map(j => <option key={j.id} value={j.id}>{j.jobNumber} — {j.description?.slice(0,40)}</option>)}
            </select>
          </div>
          <button onClick={handleUpload}
            disabled={processing}
            className="flex items-center gap-2 w-full justify-center py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium disabled:opacity-60 transition-colors">
            {processing ? <><RefreshCw className="w-4 h-4 animate-spin" /> Extracting BOM items via Claude API…</> : <><Cpu className="w-4 h-4" /> Extract BOM from drawing</>}
          </button>
          <p className="text-xs text-center text-slate-400">Platform extracts text layer (or OCR for scanned drawings) then sends to Claude API for BOM item identification and catalogue matching</p>
        </div>
      )}

      {step === 'review' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <p className="text-sm text-green-800">
              Claude API extracted <strong>{MOCK_EXTRACTED.length} items</strong> · 3 exact catalogue matches · 2 fuzzy matches · 1 unmatched. Review and accept items to write to the job BOM.
            </p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3 bg-slate-50 border-b flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-700">Extracted items</p>
              <p className="text-xs text-slate-400">{accepted.size} of {MOCK_EXTRACTED.length} accepted</p>
            </div>
            <div className="divide-y divide-slate-100">
              {MOCK_EXTRACTED.map(item => {
                const isAcc = accepted.has(item.id)
                const matchColor = item.match === 'EXACT' ? 'text-green-600 bg-green-50' : item.match === 'FUZZY' ? 'text-amber-600 bg-amber-50' : 'text-red-600 bg-red-50'
                return (
                  <div key={item.id} className={`flex items-center gap-3 px-5 py-3 ${isAcc ? 'bg-green-50/40' : ''}`}>
                    <input type="checkbox" checked={isAcc}
                      onChange={() => setAccepted(prev => {
                        const n = new Set(prev)
                        n.has(item.id) ? n.delete(item.id) : n.add(item.id)
                        return n
                      })}
                      className="w-4 h-4 accent-indigo-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900">{item.description}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{item.quantity} {item.unit}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${matchColor}`}>{item.match}</span>
                      <ConfidenceChip c={item.confidence} />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          <div className="flex gap-2">
            <button onClick={() => setStep('upload')} className="px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">Back</button>
            <button onClick={() => setStep('done')} disabled={accepted.size === 0}
              className="flex-1 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium disabled:opacity-40 transition-colors">
              Write {accepted.size} accepted item{accepted.size !== 1 ? 's' : ''} to job BOM
            </button>
          </div>
        </div>
      )}

      {step === 'done' && (
        <div className="text-center py-16 bg-white border border-slate-200 rounded-xl">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <p className="font-semibold text-slate-900">{accepted.size} items written to {MOCK_JOBS.find(j => j.id === jobId)?.jobNumber} BOM</p>
          <p className="text-sm text-slate-400 mt-1">Source tag: "Drawing extraction" · Run BOM compliance check to verify the new items</p>
          <button onClick={() => { setStep('upload'); setAccepted(new Set()); setFile(null) }}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium">
            Extract another drawing
          </button>
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// TAB 5: WI COMPLIANCE CHECKER (new)
// ─────────────────────────────────────────────────────────────────

function WIComplianceTab({ canManage }: { canManage: boolean }) {
  const [jobFilter, setJobFilter]   = useState('ALL')
  const [resultFilter, setResult]   = useState('ALL')
  const [expanded, setExpanded]     = useState<string | null>(null)
  const [running, setRunning]       = useState(false)

  const filtered = MOCK_WI_COMPLIANCE.filter(c =>
    (jobFilter === 'ALL' || c.jobId === jobFilter) &&
    (resultFilter === 'ALL' || c.result === resultFilter)
  )

  const stats = {
    compliant:      MOCK_WI_COMPLIANCE.filter(c => c.result === 'COMPLIANT').length,
    nonCompliant:   MOCK_WI_COMPLIANCE.filter(c => c.result === 'NON_COMPLIANT').length,
    review:         MOCK_WI_COMPLIANCE.filter(c => c.result === 'REVIEW').length,
  }

  const handleRun = () => {
    setRunning(true)
    setTimeout(() => setRunning(false), 2000)
  }

  return (
    <div className="space-y-4">
      <div className="bg-purple-50 border border-purple-200 rounded-xl px-4 py-3 flex items-start gap-3">
        <Cpu className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-purple-800">
          <strong>AI Work Instructions Compliance</strong> — Claude compares each job\'s estimate (labour, plant, time) against the applicable client work instructions (TMR TSI, Energex EWI). Gaps are identified and must be resolved before the estimate can be approved.
        </p>
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex gap-3 flex-wrap flex-1">
          {[
            { label: 'Compliant', value: stats.compliant,    bg: 'bg-green-50', text: 'text-green-700' },
            { label: 'Review',    value: stats.review,        bg: 'bg-amber-50', text: 'text-amber-700' },
            { label: 'Non-compliant', value: stats.nonCompliant, bg: 'bg-red-50', text: 'text-red-700' },
          ].map(s => (
            <div key={s.label} className={`${s.bg} rounded-xl px-5 py-3 text-center min-w-[100px]`}>
              <p className={`text-2xl font-bold ${s.text}`}>{s.value}</p>
              <p className="text-xs text-slate-400">{s.label}</p>
            </div>
          ))}
        </div>
        {canManage && (
          <button onClick={handleRun} disabled={running}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg disabled:opacity-60 transition-colors">
            {running ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Cpu className="w-4 h-4" />}
            {running ? 'Checking…' : 'Run WI compliance check'}
          </button>
        )}
      </div>

      <div className="flex gap-3 flex-col sm:flex-row">
        <select value={jobFilter} onChange={e => setJobFilter(e.target.value)} className="input text-sm">
          <option value="ALL">All projects</option>
          {MOCK_JOBS.map(j => <option key={j.id} value={j.id}>{j.jobNumber}</option>)}
        </select>
        <select value={resultFilter} onChange={e => setResult(e.target.value)} className="input text-sm">
          <option value="ALL">All results</option>
          <option value="COMPLIANT">Compliant</option>
          <option value="NON_COMPLIANT">Non-compliant</option>
          <option value="REVIEW">Review</option>
        </select>
        {jobFilter !== 'ALL' && (
          <button onClick={() => generateWIComplianceCertificate(jobFilter, MOCK_JOBS.find(j => j.id === jobFilter)?.jobNumber ?? jobFilter, MOCK_WI_COMPLIANCE)}
            className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">
            📋 WI compliance certificate
          </button>
        )}
      </div>

      {running && (
        <div className="flex items-center gap-3 bg-purple-50 border border-purple-200 rounded-xl px-4 py-3">
          <Cpu className="w-4 h-4 text-purple-600 animate-pulse" />
          <p className="text-sm text-purple-700">Claude API comparing estimate labour, plant, and time against WI requirements…</p>
        </div>
      )}

      <div className="space-y-3">
        {filtered.map(check => {
          const isExp = expanded === check.id
          const rm    = WI_RESULT_META[check.result]
          const RIcon = rm.icon
          const wi    = ALL_WORK_INSTRUCTIONS.find(w => w.id === check.wiId)
          const hps   = wi ? getHoldPoints(wi) : []
          const timeGap = check.wiRequiredHours > 0
            ? Math.round((check.estimatedHours / check.wiRequiredHours) * 100)
            : 100

          return (
            <div key={check.id} className={`bg-white border-2 rounded-xl overflow-hidden
              ${check.result === 'NON_COMPLIANT' ? 'border-red-300' : check.result === 'REVIEW' ? 'border-amber-300' : 'border-slate-200'}`}>
              <button onClick={() => setExpanded(isExp ? null : check.id)}
                className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-slate-50 transition-colors">
                <RIcon className={`w-5 h-5 flex-shrink-0 ${rm.text}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <WIResultBadge result={check.result} />
                    <span className="font-mono text-xs text-slate-400">{check.wiRef}</span>
                    <span className="text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">{check.jobNumber}</span>
                  </div>
                  <p className="text-sm font-semibold text-slate-900">{check.wiTitle}</p>
                  {check.gaps.length > 0 && (
                    <p className="text-xs text-red-600 mt-0.5">{check.gaps.length} gap{check.gaps.length !== 1 ? 's' : ''} identified</p>
                  )}
                </div>
                <div className="flex-shrink-0 text-right text-xs text-slate-400 hidden sm:block">
                  <div>Est: {check.estimatedHours}h vs WI: {check.wiRequiredHours}h</div>
                  <div className={timeGap < 60 ? 'text-red-600 font-semibold' : timeGap < 80 ? 'text-amber-600' : 'text-green-600'}>
                    {timeGap}% of WI allowance
                  </div>
                </div>
                {isExp ? <ChevronUp className="w-4 h-4 text-slate-300 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-300 flex-shrink-0" />}
              </button>

              {isExp && (
                <div className="border-t px-5 py-4 space-y-4">
                  {/* Metrics grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: 'Est. hours', val: `${check.estimatedHours}h`, sub: `WI: ${check.wiRequiredHours}h`, ok: timeGap >= 80 },
                      { label: 'Time vs WI', val: `${timeGap}%`, sub: timeGap < 60 ? 'HIGH RISK' : timeGap < 80 ? 'Borderline' : 'OK', ok: timeGap >= 80 },
                      { label: 'Est. crew', val: String(check.estimatedCrew), sub: `WI min: ${check.wiMinCrew}`, ok: check.estimatedCrew >= check.wiMinCrew },
                      { label: 'Hold points', val: String(hps.length), sub: 'in WI', ok: true },
                    ].map(m => (
                      <div key={m.label} className={`rounded-xl p-3 ${m.ok ? 'bg-green-50' : 'bg-red-50'}`}>
                        <p className="text-xs text-slate-400">{m.label}</p>
                        <p className={`text-lg font-bold ${m.ok ? 'text-green-700' : 'text-red-700'}`}>{m.val}</p>
                        <p className={`text-xs ${m.ok ? 'text-green-500' : 'text-red-500'}`}>{m.sub}</p>
                      </div>
                    ))}
                  </div>

                  {/* Gaps */}
                  {check.gaps.length > 0 ? (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-red-700 uppercase tracking-wider">Compliance gaps — must resolve before approval</p>
                      {check.gaps.map((g, i) => <GapRow key={i} gap={g} />)}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 rounded-xl px-4 py-3">
                      <CheckCircle className="w-4 h-4" /> No gaps identified — estimate meets all WI requirements
                    </div>
                  )}

                  {/* Hold points */}
                  {hps.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">WI hold points (must be in programme)</p>
                      <div className="space-y-1.5">
                        {hps.map(hp => (
                          <div key={hp.holdPointRef} className="flex items-start gap-2 text-xs bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                            <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0 mt-0.5" />
                            <span className="font-mono text-red-700 flex-shrink-0">{hp.holdPointRef}</span>
                            <span className="text-slate-600">{hp.holdPointDescription}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  {check.result !== 'COMPLIANT' && canManage && (
                    <div className="flex gap-2 pt-1">
                      <button className="px-3 py-1.5 bg-indigo-600 text-white text-xs rounded-lg font-medium">Update estimate</button>
                      <button className="px-3 py-1.5 border border-slate-200 text-slate-600 text-xs rounded-lg hover:bg-slate-50">Override with justification</button>
                    </div>
                  )}

                  <p className="text-xs text-slate-400">Checked {formatDate(check.checkedAt)} · WI: {check.wiRef} · {check.jobNumber}</p>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// TAB 6: WI ESTIMATING ENGINE (new)
// ─────────────────────────────────────────────────────────────────

function WIEstimatingTab({ canManage }: { canManage: boolean }) {
  const [selectedWI, setSelectedWI] = useState<WorkInstruction | null>(null)
  const [quantity, setQuantity]     = useState('1')
  const [jobId, setJobId]           = useState(MOCK_JOBS[0]?.id ?? '')
  const [generated, setGenerated]   = useState(false)

  const approvedWIs = ALL_WORK_INSTRUCTIONS.filter(w => w.status === 'APPROVED' || w.status === 'DEPLOYED')

  const estimate = useMemo(() => {
    if (!selectedWI) return null
    const qty = parseFloat(quantity) || 1

    // Extract labour lines from WI steps
    const labourByTrade: Record<string, { hours: number; rate: number; name: string }> = {}
    for (const step of selectedWI.steps) {
      if (!step.tradeCode || !step.estimatedHours || step.isHoldPoint) continue
      const rate = TRADE_RATE_MAP[step.tradeCode as keyof typeof TRADE_RATE_MAP]
      if (!rate) continue
      if (!labourByTrade[step.tradeCode]) labourByTrade[step.tradeCode] = { hours: 0, rate: (rate as any).baseRateAud, name: (rate as any).name }
      labourByTrade[step.tradeCode].hours += step.estimatedHours
    }

    const labourLines = Object.entries(labourByTrade).map(([code, data]) => ({
      trade: code, name: data.name,
      hoursPerUnit: data.hours, totalHours: data.hours * qty,
      rateAud: data.rate, totalCost: data.hours * qty * data.rate,
    }))

    const totalLabourHours = labourLines.reduce((s, l) => s + l.totalHours, 0)
    const totalLabourCost  = labourLines.reduce((s, l) => s + l.totalCost, 0)

    const holdPoints = getHoldPoints(selectedWI)
    const { done, total } = getStepCompletionCount(selectedWI)

    return { labourLines, totalLabourHours, totalLabourCost, holdPoints, stepCount: total }
  }, [selectedWI, quantity])

  return (
    <div className="space-y-5">
      <div className="bg-purple-50 border border-purple-200 rounded-xl px-4 py-3 flex items-start gap-3">
        <Cpu className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-purple-800">
          <strong>WI Estimating Engine</strong> — select a work instruction, enter the quantity, and the engine auto-populates a base estimate with the correct labour trades, crew, hours, and costs derived directly from the WI steps. Use this as the starting point for both job costing and tender preparation.
        </p>
      </div>

      {/* Step 1 — Select WI */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <span className="w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
          Select work instruction
        </h3>
        <select
          value={selectedWI?.id ?? ''}
          onChange={e => setSelectedWI(approvedWIs.find(w => w.id === e.target.value) ?? null)}
          className="input w-full text-sm">
          <option value="">— Select a work instruction —</option>
          {approvedWIs.map(wi => (
            <option key={wi.id} value={wi.id}>{wi.reference} — {wi.title}</option>
          ))}
        </select>

        {selectedWI && (
          <div className="bg-slate-50 rounded-xl p-4 text-sm space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${DISCIPLINE_META[selectedWI.discipline]?.bg} ${DISCIPLINE_META[selectedWI.discipline]?.text}`}>
                {DISCIPLINE_META[selectedWI.discipline]?.label}
              </span>
              <span className="text-xs font-mono text-slate-500">{selectedWI.reference} v{selectedWI.version}</span>
              <span className="text-xs text-slate-400">{selectedWI.estimatedDuration}</span>
            </div>
            <p className="text-slate-700">{selectedWI.title}</p>
            <div className="flex gap-4 text-xs text-slate-400">
              <span>{selectedWI.steps.length} steps</span>
              <span>{getHoldPoints(selectedWI).length} hold points</span>
              <span>{selectedWI.requiredCredentials.length} required credentials</span>
              {selectedWI.applicableRegulations.length > 0 && <span>{selectedWI.applicableRegulations[0]}</span>}
            </div>
          </div>
        )}
      </div>

      {/* Step 2 — Quantity + job */}
      {selectedWI && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <span className="w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
            Set quantity &amp; target job
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Quantity (units of this activity)</label>
              <input type="number" value={quantity} onChange={e => setQuantity(e.target.value)}
                min="1" className="input w-full text-sm" />
              <p className="text-xs text-slate-400 mt-1">e.g. 1 = one intersection, 4 = four cable joints</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Target job / tender</label>
              <select value={jobId} onChange={e => setJobId(e.target.value)} className="input w-full text-sm">
                {MOCK_JOBS.map(j => <option key={j.id} value={j.id}>{j.jobNumber}</option>)}
              </select>
            </div>
          </div>
          <button onClick={() => setGenerated(true)}
            className="flex items-center gap-2 w-full justify-center py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-colors">
            <Zap className="w-4 h-4" /> Generate WI-compliant estimate
          </button>
        </div>
      )}

      {/* Step 3 — Generated estimate */}
      {selectedWI && estimate && generated && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 flex items-center justify-between">
            <div>
              <p className="text-white font-semibold text-sm">Auto-generated estimate from {selectedWI.reference}</p>
              <p className="text-indigo-200 text-xs mt-0.5">{quantity} × {selectedWI.title}</p>
            </div>
            <button className="flex items-center gap-1.5 text-xs text-white bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg transition-colors">
              <Download className="w-3.5 h-3.5" /> Write to BOQ
            </button>
          </div>

          {/* Labour lines */}
          <div className="p-5 space-y-4">
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" /> Labour — from WI step tradeCode and estimatedHours
            </h4>
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-50 border-b text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  <th className="text-left px-4 py-2.5">Trade</th>
                  <th className="text-right px-4 py-2.5">hrs/unit</th>
                  <th className="text-right px-4 py-2.5">× qty</th>
                  <th className="text-right px-4 py-2.5">Total hrs</th>
                  <th className="text-right px-4 py-2.5">Rate</th>
                  <th className="text-right px-4 py-2.5">Cost</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {estimate.labourLines.map((line, i) => (
                    <tr key={i} className={i % 2 === 0 ? '' : 'bg-slate-50/50'}>
                      <td className="px-4 py-2.5">
                        <p className="font-medium text-slate-900">{line.name}</p>
                        <p className="text-xs text-slate-400 font-mono">{line.trade}</p>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{line.hoursPerUnit.toFixed(1)}</td>
                      <td className="px-4 py-2.5 text-right text-slate-400">{quantity}</td>
                      <td className="px-4 py-2.5 text-right font-semibold tabular-nums">{line.totalHours.toFixed(1)}</td>
                      <td className="px-4 py-2.5 text-right text-slate-500">{formatCurrency(line.rateAud)}/hr</td>
                      <td className="px-4 py-2.5 text-right font-bold text-indigo-700 tabular-nums">{formatCurrency(line.totalCost)}</td>
                    </tr>
                  ))}
                  <tr className="bg-indigo-50 font-semibold">
                    <td className="px-4 py-3 text-indigo-900">Total labour</td>
                    <td colSpan={3} className="px-4 py-3 text-right text-indigo-700">{estimate.totalLabourHours.toFixed(1)} hrs</td>
                    <td className="px-4 py-3 text-right text-xs text-indigo-500">
                      <DollarSign className="w-3 h-3 inline" />
                    </td>
                    <td className="px-4 py-3 text-right text-lg font-bold text-indigo-700">{formatCurrency(estimate.totalLabourCost)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Required credentials */}
            {selectedWI.requiredCredentials.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <Zap className="w-3.5 h-3.5" /> Required credentials (WI mandate)
                </h4>
                <div className="flex flex-wrap gap-1.5">
                  {selectedWI.requiredCredentials.map((c, i) => (
                    <span key={i} className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-1 rounded-lg">{c}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Hold points */}
            {estimate.holdPoints.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" /> Hold points — must appear in programme
                </h4>
                <div className="space-y-1.5">
                  {estimate.holdPoints.map(hp => (
                    <div key={hp.holdPointRef} className="flex items-center gap-2 text-xs bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                      <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0" />
                      <span className="font-mono text-red-700 flex-shrink-0">{hp.holdPointRef}</span>
                      <span className="text-slate-600">{hp.holdPointDescription}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tools */}
            {selectedWI.requiredTools.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <Wrench className="w-3.5 h-3.5" /> Required plant &amp; tools (WI mandate)
                </h4>
                <div className="flex flex-wrap gap-1.5">
                  {selectedWI.requiredTools.map((t, i) => (
                    <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-lg">{t}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-800">
              <strong>Estimate is WI-compliant.</strong> All labour lines derived from published WI step requirements. Add site-specific items, apply current supplier pricing, and set margin before final tender submission.
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────

type AITab = 'standards' | 'bom' | 'cable' | 'drawing' | 'wi-check' | 'wi-estimate'

export function AICompliance() {
  const { isTier } = useAuthStore()
  const [tab, setTab] = useState<AITab>('standards')
  const canManage = isTier('B_PC')

  const failCount    = MOCK_BOM_RESULTS.filter(r => r.result === 'FAIL').length
  const reviewCount  = MOCK_BOM_RESULTS.filter(r => r.result === 'REVIEW').length
  const ncCount      = MOCK_WI_COMPLIANCE.filter(c => c.result === 'NON_COMPLIANT').length
  const specUpdate   = MOCK_CLIENT_SPECS.filter(s => s.updateAvailableFlag).length

  const tabs: { id: AITab; label: string; icon: any; alert?: number }[] = [
    { id: 'standards',   label: 'Standards library',    icon: BookOpen,   alert: specUpdate },
    { id: 'bom',         label: 'BOM compliance',       icon: Shield,     alert: failCount + reviewCount },
    { id: 'cable',       label: 'Cable classifier',      icon: Zap },
    { id: 'drawing',     label: 'Drawing extractor',    icon: Upload },
    { id: 'wi-check',    label: 'WI compliance',        icon: FileText,   alert: ncCount },
    { id: 'wi-estimate', label: 'WI estimating',        icon: BarChart2 },
  ]

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-2xl font-bold text-slate-900">AI Compliance</h1>
            <span className="flex items-center gap-1 text-xs bg-purple-100 text-purple-700 px-2.5 py-0.5 rounded-full font-semibold">
              <Cpu className="w-3 h-3" /> Claude API
            </span>
          </div>
          <p className="text-sm text-slate-500">Materials compliance · Cable classification · Work instruction compliance · WI-powered estimating</p>
        </div>
      </div>

      {/* Alert banner */}
      {(failCount + ncCount) > 0 && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-800 flex-1">
            {failCount > 0 && <><strong>{failCount} BOM item{failCount !== 1 ? 's' : ''}</strong> failed compliance — blocked from PO. </>}
            {ncCount > 0 && <><strong>{ncCount} work instruction{ncCount !== 1 ? 's' : ''}</strong> non-compliant — estimate cannot be approved. </>}
            Action required before job can proceed.
          </p>
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: 'Standards loaded', value: MOCK_CLIENT_SPECS.length,   bg: 'bg-slate-50',    text: 'text-slate-700'   },
          { label: 'BOM pass rate',    value: `${Math.round((MOCK_BOM_RESULTS.filter(r=>r.result==='PASS').length / MOCK_BOM_RESULTS.length) * 100)}%`, bg: 'bg-green-50', text: 'text-green-700' },
          { label: 'BOM items checked', value: MOCK_BOM_RESULTS.length,   bg: 'bg-slate-50',    text: 'text-slate-700'   },
          { label: 'WI checks',        value: MOCK_WI_COMPLIANCE.length,  bg: 'bg-purple-50',   text: 'text-purple-700'  },
          { label: 'WI compliant',     value: `${Math.round((MOCK_WI_COMPLIANCE.filter(c=>c.result==='COMPLIANT').length / MOCK_WI_COMPLIANCE.length) * 100)}%`, bg: ncCount > 0 ? 'bg-red-50' : 'bg-green-50', text: ncCount > 0 ? 'text-red-700' : 'text-green-700' },
          { label: 'Standard updates', value: specUpdate, bg: specUpdate > 0 ? 'bg-amber-50' : 'bg-slate-50', text: specUpdate > 0 ? 'text-amber-700' : 'text-slate-500' },
        ].map(k => (
          <div key={k.label} className={`${k.bg} rounded-xl p-3`}>
            <p className={`text-xl font-bold ${k.text}`}>{k.value}</p>
            <p className="text-xs text-slate-400 mt-0.5 leading-tight">{k.label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1 flex-wrap">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg transition-colors relative flex-shrink-0
              ${tab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            <t.icon className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="hidden sm:inline">{t.label}</span>
            <span className="sm:hidden">{t.label.split(' ')[0]}</span>
            {t.alert != null && t.alert > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                {t.alert > 9 ? '9+' : t.alert}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'standards'   && <StandardsLibraryTab canManage={canManage} />}
      {tab === 'bom'         && <BOMComplianceTab />}
      {tab === 'cable'       && <CableClassifierTab />}
      {tab === 'drawing'     && <DrawingExtractorTab />}
      {tab === 'wi-check'    && <WIComplianceTab canManage={canManage} />}
      {tab === 'wi-estimate' && <WIEstimatingTab canManage={canManage} />}

    </div>
  )
}
